import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import liff from "../../../lib/liff";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";

import {
  createStripeCheckoutSession,
  getUserEmail,
  getHalloweenPageData,
  getUnconfirmedPayPayPayment,
} from "utils/api";
import AppLayout from "works/jashindp/components/halloween/AppLayout";
import InputEmail from "default/components/InputEmail";
import PayPayButton from "works/jashindp/components/halloween/PayPayButton";
import StyledButton from "default/components/StyledButton";
import Loading from "default/components/Loading";
import { useUrlParams } from "hooks/useUrlParams";
import { useTheme } from "@emotion/react";

// Constants
const STRIPE_API_KEY = process.env.REACT_APP_STRIPE_API_KEY;
const PURCHASE_METHODS = {
  STRIPE: "stripe",
  PAYPAY: "paypay",
};

// Add image size limit constant
const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3MB in bytes

// Page states enum
const PAGE_STATES = {
  LOADING: "loading",
  TICKET_NOT_FOUND: "ticket_not_found",
  EMAIL_REQUIRED: "email_required",
  PURCHASED: "purchased",
  FORM: "form",
  LANDING: "landing",
};

// Stripe initialization - outside component to avoid recreation
const stripePromise = loadStripe(STRIPE_API_KEY);

const NIGHT_18 = "2025-10-31 18:00:00"; // 18時配達
const NIGHT_20 = "2025-10-31 20:00:00"; // 20時配達
const NIGHT_22 = "2025-10-31 22:00:00"; // 22時配達

// 配達時間選択肢の表示判定関数
const isDeliveryTimeAvailable = (deliveryTimeString) => {
  const now = new Date();
  const tokyoTime = new Date(
    now.toLocaleString("en-US", { timeZone: "Asia/Tokyo" })
  );
  const deliveryTime = new Date(deliveryTimeString);

  // 配達時刻の3分前までは選択可能とする
  const cutoffTime = new Date(deliveryTime.getTime() - 3 * 60 * 1000);

  return tokyoTime <= cutoffTime;
};

const withLng = (path, lng) => {
  const hasQuery = path.includes("?");
  return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
};

// QuestionIcon Component
const QuestionIcon = ({ onClick }) => (
  <Box
    onClick={onClick}
    sx={(theme) => ({
      width: "16px",
      height: "16px",
      borderRadius: "50%",
      border: `1px solid white`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontSize: "10px",
      color: "white",
      fontWeight: "bold",
      ml: 1,
      mt: -0.5,
    })}
  >
    ?
  </Box>
);

// Common Dialog Component
const CommonDialog = ({
  open,
  onClose,
  title,
  children,
  theme,
  maxWidth = "350px",
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          width: "88%",
        },
      }}
    >
      <DialogContent sx={{ padding: 0 }}>
        <Box
          sx={{
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            border: "1px solid rgba(255, 255, 255, 0.6)",
            boxShadow:
              "0 0 15px rgba(255, 255, 255, 0.4), 0 1px 1px rgba(255, 255, 255, 0.4)",
            borderRadius: "8px",
            padding: "32px 24px",
            maxWidth,
            position: "relative",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: "8px",
              top: "8px",
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Kaisei Decol",
              fontSize: "16px",
              fontWeight: "bold",
              color: "white",
              mb: 3,
              textAlign: "center",
            }}
          >
            {title}
          </Typography>
          {children}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// InfoDialog Component
const InfoDialog = ({ open, onClose, type, theme }) => {
  const getContent = () => {
    switch (type) {
      case "halloween_costume":
        return {
          title: "ハロウィン衣装",
          content: (
            <>
              指定した衣装の詳細な部分まで
              <br />
              反映されない場合がございます。
              <br />
              なお、成人向けレベルの画像は送られません。
            </>
          ),
        };
      default:
        return { title: "", content: "" };
    }
  };

  const { title, content } = getContent();

  return (
    <CommonDialog open={open} onClose={onClose} title={title} theme={theme}>
      <Typography
        sx={{
          fontFamily: theme.typography.fontFamily.body,
          fontSize: "14px",
          color: "white",
          lineHeight: 1.8,
          textAlign: "center",
        }}
      >
        {content}
      </Typography>
    </CommonDialog>
  );
};

const usePurchase = (soulId, work, setCurrentPageState) => {
  const navigate = useNavigate();
  const [state, setState] = useState({
    isProcessing: false,
    presentData: null,
    purchaseMethod: null,
    emailRequired: false,
  });

  const purchaseWithStripe = useCallback(
    async (presentData, setValidationDialog) => {
      try {
        const { session_id } = await createStripeCheckoutSession(soulId, {
          product_type: "present",
          data: presentData,
        });
        const stripe = await stripePromise;

        const { error } = await stripe.redirectToCheckout({
          sessionId: session_id,
        });

        if (error) {
          console.error("Stripe error:", error);
          if (setValidationDialog) {
            setValidationDialog({
              open: true,
              message: `決済処理でエラーが発生しました。運営までお問い合わせください。`,
            });
          }
        }
      } catch (error) {
        console.error("Purchase error:", error);
        if (setValidationDialog) {
          setValidationDialog({
            open: true,
            message:
              "決済処理中にエラーが発生しました。運営までお問い合わせください。",
          });
        }
      }
    },
    [soulId]
  );

  const purchaseWithPayPay = useCallback(
    (presentData) => {
      const params = new URLSearchParams({
        soul_id: soulId,
        product_type: "present",
        data: JSON.stringify(presentData),
      });
      navigate(`/${work}/paypay/confirm?${params.toString()}`);
    },
    [navigate, soulId, work]
  );

  const handlePurchaseMethod = useCallback(
    async (method, presentData, setValidationDialog) => {
      setState((prev) => ({ ...prev, isProcessing: true }));

      try {
        const email = await getUserEmail();

        if (!email.email_address) {
          setState((prev) => ({
            ...prev,
            emailRequired: true,
            purchaseMethod: method,
            presentData: presentData,
            isProcessing: false,
          }));
          setCurrentPageState(PAGE_STATES.EMAIL_REQUIRED);
          window.scrollTo(0, 0);
          return;
        }

        if (method === PURCHASE_METHODS.STRIPE) {
          await purchaseWithStripe(presentData, setValidationDialog);
        } else if (method === PURCHASE_METHODS.PAYPAY) {
          purchaseWithPayPay(presentData);
        }
      } catch (error) {
        console.error("Purchase method error:", error);
        if (setValidationDialog) {
          setValidationDialog({
            open: true,
            message:
              "決済処理中にエラーが発生しました。\n時間をおいて再度お試しください。",
          });
        }
      } finally {
        setState((prev) => ({ ...prev, isProcessing: false }));
      }
    },
    [purchaseWithStripe, purchaseWithPayPay, setCurrentPageState]
  );

  const navigateToPurchase = useCallback(async () => {
    const { purchaseMethod, presentData } = state;

    if (purchaseMethod === PURCHASE_METHODS.STRIPE) {
      await purchaseWithStripe(presentData);
    } else if (purchaseMethod === PURCHASE_METHODS.PAYPAY) {
      purchaseWithPayPay(presentData);
    }
  }, [state, purchaseWithStripe, purchaseWithPayPay]);

  return {
    ...state,
    handlePurchaseMethod,
    navigateToPurchase,
    setPurchaseState: setState,
  };
};

// Component for Purchase Dialog
const PurchaseDialog = ({
  open,
  onClose,
  onPurchaseMethod,
  isProcessing,
  lng,
  t,
  theme,
}) => {
  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      title={t("common:button.dialog.select_purchase")}
      theme={theme}
      maxWidth="450px"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          alignItems: "center",
        }}
      >
        <StyledButton
          variant="contained"
          onClick={() => onPurchaseMethod(PURCHASE_METHODS.STRIPE)}
          sx={(theme) => ({
            width: "80%",
            fontSize: "16px",
            color: "white",
            border: `2px solid white`,
            background: isProcessing ? "white" : "transparent",
          })}
          disabled={isProcessing}
        >
          {t("common:button.purchase_option_credit_card")}
        </StyledButton>

        {lng === "ja" && (
          <Box
            sx={{
              width: "80%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <PayPayButton
              onClick={() => onPurchaseMethod(PURCHASE_METHODS.PAYPAY)}
              disabled={isProcessing}
            />
            <Typography
              sx={{
                mt: 1,
                fontSize: "12px",
                color: "white",
                textAlign: "center",
                fontFamily: theme.typography.fontFamily.body,
                whiteSpace: "pre-line",
              }}
            >
              {t("common:button.purchase_option_paypay")}
            </Typography>
          </Box>
        )}
      </Box>
    </CommonDialog>
  );
};

// Main Component
function Halloween() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { soulId, lng, work, character } = useUrlParams();
  const { t } = useTranslation([work, "common"]);

  const handleNavigateToTickets = () => {
    navigate(withLng(`/${work}/tickets?soul_id=${soulId}`, lng));
  };

  // Page state using enum
  const [currentPageState, setCurrentPageState] = useState(PAGE_STATES.LOADING);

  const {
    isProcessing,
    emailRequired,
    handlePurchaseMethod,
    navigateToPurchase,
    setPurchaseState,
  } = usePurchase(soulId, work, setCurrentPageState);

  const [dialogState, setDialogState] = useState({
    openDialog: false,
  });

  // Payment check state
  const [paymentCheck, setPaymentCheck] = useState({
    merchantPaymentId: null,
    showPaymentCheck: false,
  });

  // Check for unconfirmed PayPay payment
  const checkUnconfirmedPayment = useCallback(async (soulId) => {
    try {
      const { merchant_payment_id: merchantPaymentId } =
        await getUnconfirmedPayPayPayment(soulId);

      if (merchantPaymentId) {
        setPaymentCheck({
          merchantPaymentId,
          showPaymentCheck: true,
        });
      }
    } catch (error) {
      console.error("Error checking unconfirmed payment:", error);
    }
  }, []);

  // New state for the form
  const [message, setMessage] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [costumeType, setCostumeType] = useState(""); // A, B, or C
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [imageNote, setImageNote] = useState("");
  const [infoDialog, setInfoDialog] = useState({ open: false, type: null });
  const [validationDialog, setValidationDialog] = useState({
    open: false,
    message: "",
  });
  const [isMessageFocused, setIsMessageFocused] = useState(false);
  const [halloweenData, setHalloweenData] = useState(null);

  // Add image size validation function
  const handleImageUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.startsWith("image/")) {
      setValidationDialog({
        open: true,
        message:
          "画像ファイルを選択してください。\n（対応形式: JPG, PNG, GIF, WebP等）",
      });
      // Reset file input
      event.target.value = "";
      return;
    }

    // Check file size
    if (file.size > MAX_IMAGE_SIZE) {
      setValidationDialog({
        open: true,
        message: `画像サイズが大きすぎます。\n3MB以下の画像を選択してください。\n（現在のサイズ: ${(
          file.size /
          1024 /
          1024
        ).toFixed(1)}MB）`,
      });
      // Reset file input
      event.target.value = "";
      return;
    }

    setUploadedImage(file);
  }, []);

  // Add URL validation function
  const isValidUrl = useCallback((string) => {
    try {
      const url = new URL(string);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
      return false;
    }
  }, []);

  // Check if selected delivery time is still available
  useEffect(() => {
    if (deliveryTime && !isDeliveryTimeAvailable(deliveryTime)) {
      setDeliveryTime("");
    }
  }, [deliveryTime]);

  // Fetch Halloween page data on initialization
  useEffect(() => {
    const fetchHalloweenData = async () => {
      if (!soulId) return;

      // Check for unconfirmed payment first
      await checkUnconfirmedPayment(soulId);

      try {
        const data = await getHalloweenPageData(soulId);
        setHalloweenData(data); // Store the data

        switch (data.status) {
          case "purchased":
            setCurrentPageState(PAGE_STATES.PURCHASED);
            break;
          default:
            setCurrentPageState(PAGE_STATES.LANDING);
            break;
        }
      } catch (error) {
        console.error("Error fetching halloween data:", error);
        setCurrentPageState(PAGE_STATES.TICKET_NOT_FOUND);
      }
    };

    fetchHalloweenData();
  }, [soulId, checkUnconfirmedPayment]);

  const handleDialogPurchaseMethod = useCallback(
    async (method) => {
      // Convert uploaded image to base64 if needed
      let imageData = null;
      if (costumeType === "B" && uploadedImage) {
        try {
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(uploadedImage);
          });
          imageData = {
            name: uploadedImage.name,
            type: uploadedImage.type,
            size: uploadedImage.size,
            data: base64,
          };
        } catch (error) {
          console.error("Error converting image to base64:", error);
        }
      }

      // Create present data from form inputs
      const presentData = {
        present_key: "halloween_costume",
        details: {
          message: message.trim(),
          delivery_time: deliveryTime,
          costume_type: costumeType,
          image_note: imageNote.trim(),
          // Add image data based on costume type
          ...(costumeType === "B" &&
            imageData && { uploaded_image: imageData }),
          ...(costumeType === "C" &&
            imageUrl.trim() && { image_url: imageUrl.trim() }),
        },
      };

      handlePurchaseMethod(method, presentData, setValidationDialog);
      setDialogState((prev) => ({ ...prev, openDialog: false }));
    },
    [
      handlePurchaseMethod,
      message,
      deliveryTime,
      costumeType,
      imageNote,
      uploadedImage,
      imageUrl,
    ]
  );

  const handlePurchase = useCallback(() => {
    // Validate form inputs
    if (!costumeType) {
      setValidationDialog({
        open: true,
        message: "衣装を選択してください。",
      });
      return;
    }

    if (costumeType === "B" && !uploadedImage) {
      setValidationDialog({
        open: true,
        message: "画像をアップロードしてください。",
      });
      return;
    }

    if (costumeType === "C" && !imageUrl.trim()) {
      setValidationDialog({
        open: true,
        message: "画像のURLを入力してください。",
      });
      return;
    }

    if (
      costumeType === "C" &&
      imageUrl.trim() &&
      !isValidUrl(imageUrl.trim())
    ) {
      setValidationDialog({
        open: true,
        message:
          "有効なURL形式で入力してください。\n（例: https://example.com/image.jpg）",
      });
      return;
    }

    if (!message.trim()) {
      setValidationDialog({
        open: true,
        message: "メッセージを入力してください。",
      });
      return;
    }

    if (!deliveryTime) {
      setValidationDialog({
        open: true,
        message: "配達日時を選択してください。",
      });
      return;
    }

    // Open purchase method dialog directly
    setDialogState({
      openDialog: true,
    });
  }, [costumeType, uploadedImage, imageUrl, message, deliveryTime, isValidUrl]);

  const handlePaymentCheckClose = useCallback(() => {
    setPaymentCheck((prev) => ({ ...prev, showPaymentCheck: false }));
  }, []);

  const navigateToPaymentCheck = useCallback(() => {
    navigate(
      `/${work}/paypay/check?soul_id=${soulId}&merchant_payment_id=${paymentCheck.merchantPaymentId}&lng=${lng}&success_page=mypage&fail_page=birthday`
    );
  }, [navigate, work, soulId, paymentCheck.merchantPaymentId, lng]);

  const handleCloseWindow = () => {
    liff.closeWindow();
  };

  const handlePageNavigate = useCallback(() => {
    if (!halloweenData) return;

    switch (halloweenData.status) {
      case "ticket_not_found":
        setCurrentPageState(PAGE_STATES.TICKET_NOT_FOUND);
        window.scrollTo(0, 0);
        break;
      default:
        setCurrentPageState(PAGE_STATES.FORM);
        window.scrollTo(0, 0);
        break;
    }
  }, [halloweenData]);

  // Render loading state
  if (currentPageState === PAGE_STATES.LOADING) {
    return (
      <AppLayout page="Halloween" width="100%">
        <Loading />
      </AppLayout>
    );
  }

  // Render ticket not found state
  if (currentPageState === PAGE_STATES.TICKET_NOT_FOUND) {
    return (
      <AppLayout page="Halloween" width="100%">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            px: 3,
            py: 2,
          }}
        >
          {/* Title */}
          <Typography
            sx={(theme) => ({
              fontFamily: '"Rubik Wet Paint"',
              fontSize: 36,
              fontWeight: "bold",
              color: "white",
              letterSpacing: 1.2,
              textAlign: "center",
              whiteSpace: "pre-line",
              mt: 2,
              mb: 4,
            })}
          >
            {"Oops!"}
          </Typography>

          {/* Logo */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <img
              src={
                process.env.PUBLIC_URL +
                "/jashindp/halloween/logo_soulchat.webp"
              }
              alt="logo"
              style={{ height: "160px" }}
            />
          </Box>

          {/* Message */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              textAlign: "center",
            }}
          >
            <Typography
              sx={(theme) => ({
                fontFamily: theme.typography.fontFamily.body,
                fontSize: "16px",
                color: "white",
                textAlign: "center",
                whiteSpace: "pre-line",
                mb: 6,
              })}
            >
              {
                "キャラクターにプレゼントを贈るには\n先にSoulChatのチケットで交流を\n始める必要があります。"
              }
            </Typography>

            <StyledButton
              onClick={() => handleNavigateToTickets()}
              sx={(theme) => ({
                width: "70%",
                color: "white",
                border: `2px solid white`,
              })}
            >
              {"チケット購入"}
            </StyledButton>
          </Box>
        </Box>
      </AppLayout>
    );
  }

  // Render email input state
  if (currentPageState === PAGE_STATES.EMAIL_REQUIRED) {
    return (
      <AppLayout page="Halloween" width="88%">
        <InputEmail soulId={soulId} onClickPurchase={navigateToPurchase} />
      </AppLayout>
    );
  }

  // Render already purchased state
  if (currentPageState === PAGE_STATES.PURCHASED) {
    return (
      <AppLayout page="Halloween" width="100%">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            px: 3,
            py: 2,
          }}
        >
          {/* Title */}
          <Typography
            sx={(theme) => ({
              fontFamily: '"Rubik Wet Paint"',
              fontSize: 36,
              fontWeight: "bold",
              color: "white",
              letterSpacing: 1.2,
              textAlign: "center",
              whiteSpace: "pre-line",
              mt: 2,
              mb: 4,
            })}
          >
            {"Thank you!"}
          </Typography>

          {/* Logo */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <img
              src={
                process.env.PUBLIC_URL +
                "/jashindp/halloween/logo_halloween.webp"
              }
              alt="logo"
              style={{ height: "160px" }}
            />
          </Box>

          {/* Success message */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              textAlign: "center",
            }}
          >
            <Typography
              sx={(theme) => ({
                fontFamily: theme.typography.fontFamily.body,
                fontSize: "18px",
                color: "white",
                textAlign: "center",
                whiteSpace: "pre-line",
                mb: 2,
              })}
            >
              {"配達の予約を受け付けました。\n新しいクエストをご確認ください。"}
            </Typography>

            <Typography
              sx={(theme) => ({
                fontFamily: theme.typography.fontFamily.body,
                fontSize: "18px",
                color: "white",
                textAlign: "center",
                mt: 2,
                mb: 6,
              })}
            >
              Happy Halloween!
            </Typography>

            <StyledButton
              onClick={() => handleCloseWindow()}
              sx={(theme) => ({
                width: "70%",
                color: "white",
                border: `2px solid white`,
              })}
            >
              {"チャットに戻る"}
            </StyledButton>
          </Box>
        </Box>
      </AppLayout>
    );
  }

  // Render form state
  if (currentPageState === PAGE_STATES.FORM) {
    return (
      <AppLayout page="Halloween" width="100%">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            px: 3,
            py: 2,
          }}
        >
          {/* Title */}
          <Typography
            sx={(theme) => ({
              fontFamily: '"Rubik Wet Paint"',
              fontSize: 36,
              fontWeight: "bold",
              color: "white",
              letterSpacing: 1.2,
              textAlign: "center",
              whiteSpace: "pre-line",
              mt: 2,
              mb: 4,
            })}
          >
            {"Halloween\nPresent"}
          </Typography>

          {/* Logo */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <img
              src={
                process.env.PUBLIC_URL +
                "/jashindp/halloween/logo_halloween.webp"
              }
              alt="logo"
              style={{ height: "160px" }}
            />
          </Box>

          {/* Price */}
          <Box
            sx={{
              mb: 4,
              textAlign: "center",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
              <Typography
                variant="h4"
                sx={(theme) => ({
                  fontFamily: "Kaisei Decol",
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "white",
                  lineHeight: 1.5,
                })}
              >
                ハロウィン衣装の配達
                <br />
                ￥550
              </Typography>
              <QuestionIcon
                onClick={() =>
                  setInfoDialog({ open: true, type: "halloween_costume" })
                }
              />
            </Box>
          </Box>

          {/* Costume Selection Section - Step 1 */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "400px",
              mb: 6,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              sx={(theme) => ({
                fontFamily: "Kaisei Decol",
                fontSize: "14px",
                color: "white",
                fontWeight: "bold",
              })}
            >
              Step 1
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={(theme) => ({
                  fontFamily: "Kaisei Decol",
                  fontSize: "22px",
                  fontWeight: "bold",
                  color: "white",
                })}
              >
                送る衣装を指定
              </Typography>
            </Box>

            {/* Costume Options */}
            <FormControl component="fieldset" sx={{ width: "80%" }}>
              <RadioGroup
                value={costumeType}
                onChange={(e) => setCostumeType(e.target.value)}
              >
                {/* Option A: Succubus Costume */}
                <Box
                  sx={{
                    width: "100%",
                    mb: 3,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <FormControlLabel
                    value="A"
                    control={
                      <Radio
                        sx={{
                          color: "white",
                          "&.Mui-checked": {
                            color: "white",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={(theme) => ({
                          color: "white",
                          fontFamily: theme.typography.fontFamily.body,
                          fontSize: "16px",
                        })}
                      >
                        A. サキュバス衣装
                      </Typography>
                    }
                  />
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={
                        process.env.PUBLIC_URL +
                        "/jashindp/halloween/succubus.webp"
                      }
                      alt="succubus costume"
                      style={{
                        maxWidth: "200px",
                        height: "auto",
                        borderRadius: "8px",
                      }}
                    />
                  </Box>
                </Box>

                {/* Option B: Image Upload */}
                <Box
                  sx={{
                    width: "100%",
                    mb: 3,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <FormControlLabel
                    value="B"
                    control={
                      <Radio
                        sx={{
                          color: "white",
                          "&.Mui-checked": {
                            color: "white",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={(theme) => ({
                          color: "white",
                          fontFamily: theme.typography.fontFamily.body,
                          fontSize: "16px",
                        })}
                      >
                        B. 好きな衣装画像をアップロード
                      </Typography>
                    }
                  />
                  {costumeType === "B" && (
                    <Box
                      sx={{ mt: 2, display: "flex", justifyContent: "center" }}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{
                          color: "white",
                          backgroundColor: "transparent",
                          border: "1px solid rgba(255, 255, 255, 0.6)",
                          boxShadow:
                            "0 0 15px rgba(255, 255, 255, 0.4), 0 1px 1px rgba(255, 255, 255, 0.4)",

                          borderRadius: "4px",
                          padding: "16px",
                          width: "100%",
                        }}
                      />
                    </Box>
                  )}
                </Box>

                {/* Option C: URL Input */}
                <Box
                  sx={{
                    width: "100%",
                    mb: 3,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <FormControlLabel
                    value="C"
                    control={
                      <Radio
                        sx={{
                          color: "white",
                          "&.Mui-checked": {
                            color: "white",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={(theme) => ({
                          color: "white",
                          fontFamily: theme.typography.fontFamily.body,
                          fontSize: "16px",
                        })}
                      >
                        C. 画像のURL指定
                      </Typography>
                    }
                  />
                  {costumeType === "C" && (
                    <Box
                      sx={(theme) => ({
                        mt: 2,
                        width: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.6)",
                        boxShadow:
                          "0 0 15px rgba(255, 255, 255, 0.4), 0 1px 1px rgba(255, 255, 255, 0.4)",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                      })}
                    >
                      <TextField
                        fullWidth
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        placeholder="https://....."
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            border: "none",
                            color: "white",
                            "& fieldset": {
                              border: "none",
                            },
                            "&:hover fieldset": {
                              border: "none",
                            },
                            "&.Mui-focused fieldset": {
                              border: "none",
                            },
                          },
                          "& .MuiInputBase-input": {
                            textAlign: "left",
                            color: "white",
                            fontFamily: theme.typography.fontFamily.body,
                          },
                          "& input::placeholder": {
                            color: "white",
                            opacity: 0.7,
                            fontFamily: theme.typography.fontFamily.body,
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>

                {/* Image Note Section */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      mt: 2,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={(theme) => ({
                        fontFamily: theme.typography.fontFamily.body,
                        fontSize: "16px",
                        color: "white",
                        fontWeight: "bold",
                        textAlign: "left",
                      })}
                    >
                      画像に関する補足
                    </Typography>
                  </Box>
                  <Box
                    sx={(theme) => ({
                      width: "100%",
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.6)",
                      boxShadow:
                        "0 0 15px rgba(255, 255, 255, 0.4), 0 1px 1px rgba(255, 255, 255, 0.4)",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "8px",
                    })}
                  >
                    <TextField
                      multiline
                      fullWidth
                      value={imageNote}
                      onChange={(e) => setImageNote(e.target.value)}
                      placeholder="補足があれば記載してください"
                      sx={(theme) => ({
                        "& .MuiOutlinedInput-root": {
                          border: "none",
                          "& fieldset": {
                            border: "none",
                          },
                          "&:hover fieldset": {
                            border: "none",
                          },
                          "&.Mui-focused fieldset": {
                            border: "none",
                          },
                        },
                        "& .MuiInputBase-input": {
                          textAlign: "center",
                          color: "white",
                          fontFamily: theme.typography.fontFamily.body,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          textAlign: "center",
                          color: "white",
                          fontFamily: theme.typography.fontFamily.body,
                          resize: "none",
                        },
                        "& input::placeholder, & textarea::placeholder": {
                          color: "white",
                          opacity: 0.7,
                          textAlign: "center",
                          fontFamily: theme.typography.fontFamily.body,
                        },
                      })}
                    />
                  </Box>
                </Box>
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Message Section */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "400px",
              mb: 6,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              sx={(theme) => ({
                fontFamily: "Kaisei Decol",
                fontSize: "14px",
                color: "white",
                fontWeight: "bold",
              })}
            >
              Step 2
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={(theme) => ({
                  fontFamily: "Kaisei Decol",
                  fontSize: "22px",
                  fontWeight: "bold",
                  color: "white",
                })}
              >
                メッセージを作成
              </Typography>
            </Box>
            <Box
              sx={(theme) => ({
                width: "92%",
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.6)",
                boxShadow:
                  "0 0 15px rgba(255, 255, 255, 0.4), 0 1px 1px rgba(255, 255, 255, 0.4)",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "120px",
                padding: "8px",
              })}
            >
              <TextField
                multiline
                fullWidth
                value={message}
                onChange={(e) => {
                  if (e.target.value.length <= 50) {
                    setMessage(e.target.value);
                  }
                }}
                onFocus={() => setIsMessageFocused(true)}
                onBlur={() => setIsMessageFocused(false)}
                placeholder={
                  isMessageFocused
                    ? ""
                    : "誕生日おめでとう！\nゆりねに似合うと思って送る！\n-太郎より-"
                }
                sx={(theme) => ({
                  "& .MuiOutlinedInput-root": {
                    border: "none",
                    color: "white",
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover fieldset": {
                      border: "none",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none",
                    },
                  },
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                    color: "white",
                    fontFamily: theme.typography.fontFamily.body,
                  },
                  "& .MuiInputBase-inputMultiline": {
                    textAlign: "center",
                    color: "white",
                    fontFamily: theme.typography.fontFamily.body,
                    resize: "none",
                  },
                  "& input::placeholder, & textarea::placeholder": {
                    color: "white",
                    opacity: 0.7,
                    textAlign: "center",
                    fontFamily: theme.typography.fontFamily.body,
                  },
                })}
              />
            </Box>
            <Box
              sx={{ width: "92%", display: "flex", justifyContent: "flex-end" }}
            >
              <Typography
                variant="caption"
                sx={(theme) => ({
                  mt: 1,
                  color: "white",
                  opacity: 0.7,
                  fontFamily: theme.typography.fontFamily.body,
                })}
              >
                {message.length}/50
              </Typography>
            </Box>
          </Box>

          {/* Delivery Time Section */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "400px",
              mb: 6,
              textAlign: "center",
            }}
          >
            <Typography
              sx={(theme) => ({
                fontFamily: "Kaisei Decol",
                fontSize: "14px",
                color: "white",
                fontWeight: "bold",
              })}
            >
              Step 3
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={(theme) => ({
                  fontFamily: "Kaisei Decol",
                  fontSize: "22px",
                  fontWeight: "bold",
                  color: "white",
                })}
              >
                配達日時を選択
              </Typography>
            </Box>
            <FormControl component="fieldset">
              <RadioGroup
                value={deliveryTime}
                onChange={(e) => setDeliveryTime(e.target.value)}
              >
                {isDeliveryTimeAvailable(NIGHT_18) && (
                  <FormControlLabel
                    value={NIGHT_18}
                    sx={{
                      mb: 1,
                    }}
                    control={
                      <Radio
                        sx={(theme) => ({
                          color: "white",
                          "&.Mui-checked": {
                            color: "white",
                          },
                        })}
                      />
                    }
                    label={
                      <Typography
                        sx={(theme) => ({
                          color: "white",
                          fontFamily: theme.typography.fontFamily.body,
                          fontSize: "16px",
                        })}
                      >
                        10月31日 18:00頃
                      </Typography>
                    }
                  />
                )}
                {isDeliveryTimeAvailable(NIGHT_20) && (
                  <FormControlLabel
                    value={NIGHT_20}
                    sx={{
                      mb: 1,
                    }}
                    control={
                      <Radio
                        sx={(theme) => ({
                          color: "white",
                          "&.Mui-checked": {
                            color: "white",
                          },
                        })}
                      />
                    }
                    label={
                      <Typography
                        sx={(theme) => ({
                          color: "white",
                          fontFamily: theme.typography.fontFamily.body,
                          fontSize: "16px",
                        })}
                      >
                        10月31日 20:00頃
                      </Typography>
                    }
                  />
                )}
                {isDeliveryTimeAvailable(NIGHT_22) && (
                  <FormControlLabel
                    value={NIGHT_22}
                    control={
                      <Radio
                        sx={(theme) => ({
                          color: "white",
                          "&.Mui-checked": {
                            color: "white",
                          },
                        })}
                      />
                    }
                    label={
                      <Typography
                        sx={(theme) => ({
                          color: "white",
                          fontFamily: theme.typography.fontFamily.body,
                          fontSize: "16px",
                        })}
                      >
                        10月31日 22:00頃
                      </Typography>
                    }
                  />
                )}
              </RadioGroup>
              {!isDeliveryTimeAvailable(NIGHT_18) &&
                !isDeliveryTimeAvailable(NIGHT_20) &&
                !isDeliveryTimeAvailable(NIGHT_22) && (
                  <Typography
                    sx={(theme) => ({
                      color: "white",
                      fontFamily: theme.typography.fontFamily.body,
                      fontSize: "16px",
                      textAlign: "center",
                      opacity: 0.7,
                      mt: 2,
                    })}
                  >
                    配達可能な時間帯が終了しました
                  </Typography>
                )}
            </FormControl>
          </Box>

          {/* Quest Message Section */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "400px",
              mb: 6,
              textAlign: "center",
            }}
          >
            <Typography
              sx={(theme) => ({
                fontFamily: "Kaisei Decol",
                fontSize: "14px",
                color: "white",
                fontWeight: "bold",
              })}
            >
              Step 4
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={(theme) => ({
                  fontFamily: "Kaisei Decol",
                  fontSize: "22px",
                  fontWeight: "bold",
                  color: "white",
                })}
              >
                当日のクエストに挑戦
              </Typography>
            </Box>

            <Typography
              sx={(theme) => ({
                fontFamily: theme.typography.fontFamily.body,
                fontSize: "16px",
                color: "white",
                textAlign: "center",
                whiteSpace: "pre-line",
                lineHeight: 1.6,
              })}
            >
              {
                "当日届けた衣装を着た写真をもらう\nことがあなたへのクエスト！\n（購入後クエストが追加されます）"
              }
            </Typography>
          </Box>

          {/* Purchase Button */}
          <StyledButton
            onClick={handlePurchase}
            sx={{
              backgroundColor: "#DF5A09",
              width: "60%",
              color: "white",
              borderRadius: "12px",
              padding: "12px 24px",
              fontFamily: "Kaisei Tokumin, serif",
              fontSize: "18px",
              fontWeight: "bold",
              border: "none",
              minWidth: "250px",
              boxShadow:
                "0 4px 12px rgba(223, 90, 9, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)",
              "&:hover": {
                backgroundColor: "#C54A08",
                boxShadow:
                  "0 6px 16px rgba(223, 90, 9, 0.5), 0 3px 8px rgba(0, 0, 0, 0.4)",
              },
            }}
          >
            ゆりねに衣装を贈る!
          </StyledButton>
        </Box>
        {/* Purchase Method Dialog */}
        <PurchaseDialog
          open={dialogState.openDialog}
          onClose={() =>
            setDialogState((prev) => ({ ...prev, openDialog: false }))
          }
          onPurchaseMethod={handleDialogPurchaseMethod}
          isProcessing={isProcessing}
          lng={lng}
          t={t}
          theme={theme}
        />

        {/* Info Dialog */}
        <InfoDialog
          open={infoDialog.open}
          onClose={() => setInfoDialog({ open: false, type: null })}
          type={infoDialog.type}
          theme={theme}
        />

        {/* Payment Check Dialog */}
        <CommonDialog
          open={paymentCheck.showPaymentCheck}
          onClose={handlePaymentCheckClose}
          title="PayPay購入による処理が正常に完了していない可能性があります。"
          theme={theme}
          maxWidth="400px"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "center",
            }}
          >
            <StyledButton
              variant="contained"
              onClick={navigateToPaymentCheck}
              sx={(theme) => ({
                width: "80%",
                fontSize: "16px",
                color: theme.Dialog.button.color,
                border: `2px solid ${theme.Dialog.button.color}`,
                background: "transparent",
              })}
            >
              購入状況を確認
            </StyledButton>
          </Box>
        </CommonDialog>

        {/* Validation Dialog */}
        <CommonDialog
          open={validationDialog.open}
          onClose={() => setValidationDialog({ open: false, message: "" })}
          title="入力エラー"
          theme={theme}
        >
          <Typography
            sx={{
              fontFamily: theme.typography.fontFamily.body,
              fontSize: "14px",
              color: "white",
              lineHeight: 1.8,
              textAlign: "center",
            }}
          >
            {validationDialog.message}
          </Typography>
        </CommonDialog>
      </AppLayout>
    );
  }

  // Main render (only shown when already_purchased is false)
  return (
    <AppLayout page="Halloween" width="100%">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          px: 3,
          py: 2,
        }}
      >
        {/* Title */}
        <Typography
          sx={(theme) => ({
            fontFamily: '"Rubik Wet Paint"',
            fontSize: 36,
            fontWeight: "bold",
            color: "white",
            letterSpacing: 1.2,
            textAlign: "center",
            whiteSpace: "pre-line",
            mt: 2,
            mb: 4,
          })}
        >
          {"Halloween\nPresent"}
        </Typography>

        {/* Description */}
        <Box
          sx={{
            width: "60%",
            maxWidth: "400px",
            mb: 4,
            textAlign: "center",
            display: "flex",
          }}
        >
          <Typography
            sx={(theme) => ({
              fontFamily: theme.typography.fontFamily.body,
              fontSize: 16,
              color: "white",
              textAlign: "center",
              whiteSpace: "pre-line",
            })}
          >
            {
              "10月31日はゆりねの誕生日！\n\nまだハロウィンの衣装を決めていないゆりねに...\n\n贈りたいハロウィン衣装を\nプレゼントしよう！"
            }
          </Typography>
        </Box>

        {/* ここから追加 */}

        {/* Sample 1: Image left, text right */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            maxWidth: "400px",
            mb: 3,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <img
              src={process.env.PUBLIC_URL + "/jashindp/halloween/sample1.webp"}
              alt="sample costume 1"
              style={{ width: "100%", height: "auto", borderRadius: "8px" }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={(theme) => ({
                fontFamily: theme.typography.fontFamily.body,
                fontSize: 16,
                color: "white",
                textAlign: "center",
                whiteSpace: "pre-line",
              })}
            >
              {"例えば\nこんな衣装を贈ると..."}
            </Typography>
          </Box>
        </Box>

        {/* Sample 2: Text left, image right */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            maxWidth: "400px",
            mb: 3,
            gap: 1,
          }}
        >
          <Box sx={{ flex: 0.8 }}>
            <Typography
              sx={(theme) => ({
                fontFamily: theme.typography.fontFamily.body,
                fontSize: 16,
                color: "white",
                textAlign: "center",
                whiteSpace: "pre-line",
              })}
            >
              {"こんな風に\n着てくれた写真を\nゆりねが送って\nくれるかも！"}
            </Typography>
          </Box>
          <Box sx={{ flex: 1.2 }}>
            <img
              src={process.env.PUBLIC_URL + "/jashindp/halloween/sample2.webp"}
              alt="sample costume 2"
              style={{ width: "100%", height: "auto", borderRadius: "8px" }}
            />
          </Box>
        </Box>

        {/* Sample 3: Image left, text right */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            maxWidth: "400px",
            mb: 6,
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1.2 }}>
            <img
              src={process.env.PUBLIC_URL + "/jashindp/halloween/sample3.webp"}
              alt="sample costume 3"
              style={{ width: "100%", height: "auto", borderRadius: "8px" }}
            />
          </Box>
          <Box sx={{ flex: 0.8 }}>
            <Typography
              sx={(theme) => ({
                fontFamily: theme.typography.fontFamily.body,
                fontSize: 16,
                color: "white",
                textAlign: "center",
                whiteSpace: "pre-line",
              })}
            >
              {"自分で描いた絵でも\n作った服の写真でも\n画像のURLでもOK!"}
            </Typography>
          </Box>
        </Box>

        {/* Delivery message */}
        <Box sx={{ mb: 3, textAlign: "center" }}>
          <Typography
            sx={(theme) => ({
              fontFamily: theme.typography.fontFamily.body,
              fontSize: 16,
              color: "white",
              textAlign: "center",
              whiteSpace: "pre-line",
            })}
          >
            {"あなたが指定した衣装を\nハロウィン当日、ゆりねにお届けします！"}
          </Typography>
        </Box>

        {/* Sample 4 image */}
        <Box sx={{ mb: 3 }}>
          <img
            src={process.env.PUBLIC_URL + "/jashindp/halloween/sample4.webp"}
            alt="sample costume 4"
            style={{
              width: "100%",
              height: "auto",
              borderRadius: "8px",
              maxWidth: "300px",
            }}
          />
        </Box>

        {/* Deadline message */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography
            sx={(theme) => ({
              fontFamily: theme.typography.fontFamily.body,
              fontSize: 16,
              mb: 4,
              color: "white",
              textAlign: "center",
              whiteSpace: "pre-line",
            })}
          >
            {"10月30日まで\n配達受付中!!"}
          </Typography>
        </Box>

        {/* Halloween costume button */}
        <Box sx={{ mb: 4 }}>
          <StyledButton
            onClick={handlePageNavigate}
            sx={{
              backgroundColor: "#DF5A09",
              color: "white",
              borderRadius: "12px",
              padding: "12px 24px",
              fontFamily: "Kaisei Tokumin, serif",
              fontSize: "18px",
              fontWeight: "bold",
              border: "none",
              minWidth: "250px",
              boxShadow:
                "0 4px 12px rgba(223, 90, 9, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)",
              "&:hover": {
                backgroundColor: "#C54A08",
                boxShadow:
                  "0 6px 16px rgba(223, 90, 9, 0.5), 0 3px 8px rgba(0, 0, 0, 0.4)",
              },
            }}
          >
            ハロウィン衣装を贈る!
          </StyledButton>
        </Box>
      </Box>
    </AppLayout>
  );
}

export default Halloween;
